import { RegistrationRequest, UploadData } from '../../../../store/uploadStore';
import { calcHash } from '../../../../libs/file';

/**
 * Extracts files that are registered but not in the upload queue
 */
const getRegisteredNotQueuedFiles = (upload: UploadData) => {
  if (!upload.upload) return [];

  const queueHashSet = new Set(upload.queue.map((file) => calcHash(file.file)));

  return Object.entries(upload.upload.photos)
    .filter(([, { hash }]) => !queueHashSet.has(hash))
    .map(([, file]) => file);
};

/**
 * Calculates the total size of all files in the upload
 */
export const getUploadTotalSize = (
  upload: UploadData,
  registrationRequests: Map<Promise<unknown>, RegistrationRequest> | undefined,
) => {
  if (!upload.upload) return 0;

  const registeredNotQueued = getRegisteredNotQueuedFiles(upload);

  const registeredButNotQueuedTotal = registeredNotQueued.reduce((acc, { size }) => acc + (size ?? 0), 0);

  const queuedTotal = upload.queue.reduce((acc, { size }) => acc + (size ?? 0), 0);

  const registrationSizes = Array.from(registrationRequests?.values() ?? []).reduce(
    (acc, { filesSizeTotal }) => acc + (filesSizeTotal ?? 0),
    0,
  );

  return registeredButNotQueuedTotal + queuedTotal + registrationSizes;
};

/**
 * Calculates the size of already uploaded data
 */
export const getUploadedSize = (upload: UploadData) => {
  if (!upload.upload) return 0;

  const registeredNotQueued = getRegisteredNotQueuedFiles(upload);

  const registeredButNotQueuedTotal = registeredNotQueued.reduce(
    (acc, { size, uploaded }) => acc + (uploaded ? size ?? 0 : 0),
    0,
  );

  const queuedTotal = upload.queue.reduce((acc, { uploadedSize }) => acc + uploadedSize, 0);

  return registeredButNotQueuedTotal + queuedTotal;
};

/**
 * Calculates the upload progress as a percentage
 */
export const getUploadProgress = (upload: UploadData | undefined) => {
  if (!upload?.upload || !upload.meta.uploadSize || !upload.meta.uploadedSize) return 0;

  const { uploadedSize, uploadSize } = upload.meta;

  return (uploadedSize / uploadSize) * 100;
};
